import { CategoryData, TeamMemberData, TeamStatsData, PerformanceAlert } from '../components/performance'
import { getEmployeesByPerformanceCategory, getAllMonumentaEmployees, getCurrentUserSubordinates } from './real-data-service'

// Performance Level Configuration based on CSV analysis
export const PERFORMANCE_LEVELS = {
  HIGH_PERFORMANCE: { min: 90, label: 'Alta Performance', color: 'bg-success/20 text-success', icon: '🏆' },
  POTENTIAL: { min: 80, label: 'Potencial', color: 'bg-brand-primary/20 text-brand-primary', icon: '⭐' },
  GROWTH: { min: 70, label: 'Crescimento', color: 'bg-brand-blue/20 text-brand-blue', icon: '📈' },
  FULFILLS: { min: 60, label: 'Cumpre Função', color: 'bg-info/20 text-info', icon: '✅' },
  ANALYSIS: { min: 40, label: 'Sob Análise', color: 'bg-warning/20 text-warning', icon: '⚠️' },
  MISALIGNED: { min: 0, label: 'Desalinhado', color: 'bg-error/20 text-error', icon: '🚨' }
} as const

// Category configurations based on CSV structure (I001-I010)
export const PERFORMANCE_CATEGORIES = {
  I001: { name: 'Técnico', weight: 20, icon: 'Code', description: 'Decisão técnica/mentoria registrada' },
  I002: { name: 'Comunicação', weight: 15, icon: 'MessageCircle', description: 'Brief/recap claro e completo' },
  I003: { name: 'Processos & Wrike', weight: 10, icon: 'Settings', description: 'Higiene no Wrike e aderência a processos' },
  I004: { name: 'Projetos', weight: 15, icon: 'FolderOpen', description: 'Marcos no prazo e critérios cumpridos' },
  I005: { name: 'Resultados & Finanças', weight: 10, icon: 'DollarSign', description: 'Variação orçamentária controlada' },
  I006: { name: 'Comportamental', weight: 10, icon: 'Users', description: 'CNV, accountability, colaboração' },
  I007: { name: 'Conflitos & Ética', weight: 5, icon: 'Shield', description: 'Zero triangulação; confidencialidade' },
  I008: { name: 'Fit Cultural', weight: 5, icon: 'Heart', description: 'Vive Valores Monu; evolução do todo' },
  I009: { name: 'Diversidade & Inclusão', weight: 5, icon: 'Star', description: 'Linguagem inclusiva; acolhimento' },
  I010: { name: 'Feedback do Cliente', weight: 5, icon: 'Target', description: 'CSAT/NPS e percepção do cliente' }
} as const

/**
 * Calculate performance level based on score
 */
export const getPerformanceLevel = (score: number): { level: string; config: typeof PERFORMANCE_LEVELS[keyof typeof PERFORMANCE_LEVELS] } => {
  if (score >= PERFORMANCE_LEVELS.HIGH_PERFORMANCE.min) {
    return { level: 'HIGH_PERFORMANCE', config: PERFORMANCE_LEVELS.HIGH_PERFORMANCE }
  } else if (score >= PERFORMANCE_LEVELS.POTENTIAL.min) {
    return { level: 'POTENTIAL', config: PERFORMANCE_LEVELS.POTENTIAL }
  } else if (score >= PERFORMANCE_LEVELS.GROWTH.min) {
    return { level: 'GROWTH', config: PERFORMANCE_LEVELS.GROWTH }
  } else if (score >= PERFORMANCE_LEVELS.FULFILLS.min) {
    return { level: 'FULFILLS', config: PERFORMANCE_LEVELS.FULFILLS }
  } else if (score >= PERFORMANCE_LEVELS.ANALYSIS.min) {
    return { level: 'ANALYSIS', config: PERFORMANCE_LEVELS.ANALYSIS }
  } else {
    return { level: 'MISALIGNED', config: PERFORMANCE_LEVELS.MISALIGNED }
  }
}

/**
 * Calculate weighted performance score from categories
 */
export const calculatePerformanceScore = (categories: CategoryData[]): number => {
  const totalWeight = categories.reduce((sum, cat) => sum + cat.weight, 0)
  const weightedSum = categories.reduce((sum, cat) => sum + (cat.score * cat.weight), 0)
  
  return totalWeight > 0 ? Math.round(weightedSum / totalWeight) : 0
}

/**
 * Transform CSV data to CategoryData format
 */
export const transformCsvToCategories = (csvData: any[]): CategoryData[] => {
  return csvData
    .filter(row => row.itemId && row.nota !== undefined)
    .map(row => ({
      id: row.itemId || row['Item ID'],
      name: PERFORMANCE_CATEGORIES[row.itemId as keyof typeof PERFORMANCE_CATEGORIES]?.name || row.categoria || 'Categoria',
      score: parseFloat(row.nota || row['Nota (0-5)']) * 20, // Convert 0-5 scale to 0-100
      weight: PERFORMANCE_CATEGORIES[row.itemId as keyof typeof PERFORMANCE_CATEGORIES]?.weight || 10,
      evidence: row.comentarios || row['Comentário/Evidências'],
      actions: row.acoes ? row.acoes.split(';').map((a: string) => a.trim()) : undefined
    }))
}

/**
 * Calculate team statistics
 */
export const calculateTeamStats = (teamMembers: TeamMemberData[]): TeamStatsData => {
  const activeMembers = teamMembers.filter(m => m.score > 0)
  const totalScore = activeMembers.reduce((sum, member) => sum + member.score, 0)
  const averageScore = activeMembers.length > 0 ? Math.round(totalScore / activeMembers.length) : 0

  // Calculate distribution by level
  const distributionByLevel = activeMembers.reduce((acc, member) => {
    const { level } = getPerformanceLevel(member.score)
    const key = level.toLowerCase()
    acc[key] = (acc[key] || 0) + 1
    return acc
  }, {} as { [key: string]: number })

  // Calculate meeting stats from team data - in real implementation, these would come from meeting system data
  const completedOneOnOnes = Math.floor(activeMembers.length * 0.8) // 80% completion rate
  const pendingActions = Math.floor(activeMembers.length * 0.2) // 20% have pending actions
  const upcomingReviews = Math.floor(activeMembers.length * 0.15) // 15% have upcoming reviews

  return {
    totalMembers: teamMembers.length,
    activeMembers: activeMembers.length,
    averageScore,
    teamTrend: 'up', // This would be calculated based on historical data
    distributionByLevel,
    completedOneOnOnes,
    pendingActions,
    upcomingReviews
  }
}

/**
 * Generate performance alerts based on team data
 */
export const generatePerformanceAlerts = (teamMembers: TeamMemberData[]): PerformanceAlert[] => {
  const alerts: PerformanceAlert[] = []

  teamMembers.forEach(member => {
    // Performance drop alerts
    if (member.previousScore && member.score < member.previousScore - 10) {
      alerts.push({
        id: `perf-drop-${member.id}`,
        type: 'performance_drop',
        severity: member.score < 50 ? 'critical' : 'high',
        title: 'Queda de Performance Detectada',
        description: `Performance de ${member.name} caiu ${member.previousScore - member.score} pontos`,
        memberName: member.name,
        memberRole: member.role,
        memberId: member.id,
        metric: 'Score Performance',
        previousValue: member.previousScore,
        currentValue: member.score,
        createdAt: '2 dias',
        resolved: false
      })
    }

    // Low performance alerts
    if (member.score < 60) {
      alerts.push({
        id: `low-perf-${member.id}`,
        type: 'goal_missed',
        severity: member.score < 40 ? 'critical' : 'high',
        title: 'Performance Abaixo da Meta',
        description: `${member.name} está com score ${member.score}, abaixo do esperado`,
        memberName: member.name,
        memberRole: member.role,
        memberId: member.id,
        createdAt: '1 semana',
        resolved: false
      })
    }

    // Missing review alerts (mock data)
    const daysSinceLastUpdate = Math.floor(Math.random() * 45) + 1
    if (daysSinceLastUpdate > 30) {
      alerts.push({
        id: `review-${member.id}`,
        type: 'missing_review',
        severity: daysSinceLastUpdate > 45 ? 'high' : 'medium',
        title: 'Avaliação em Atraso',
        description: `${member.name} não tem avaliação há ${daysSinceLastUpdate} dias`,
        memberName: member.name,
        memberRole: member.role,
        memberId: member.id,
        daysOverdue: daysSinceLastUpdate - 30,
        createdAt: `${daysSinceLastUpdate} dias`,
        resolved: false
      })
    }
  })

  return alerts.sort((a, b) => {
    const severityOrder = { critical: 4, high: 3, medium: 2, low: 1 }
    return severityOrder[b.severity] - severityOrder[a.severity]
  })
}

/**
 * Format performance data for charts
 */
export const formatPerformanceChartData = (categories: CategoryData[]) => {
  return categories.map(cat => ({
    name: cat.name.replace('& ', '\n'),
    score: cat.score,
    weight: cat.weight,
    fill: cat.score >= 80 ? '#10B981' : cat.score >= 60 ? '#D2FA52' : cat.score >= 40 ? '#F59E0B' : '#EF4444'
  }))
}

/**
 * Calculate trend based on historical data
 */
export const calculateTrend = (current: number, previous?: number): 'up' | 'down' | 'neutral' => {
  if (!previous) return 'neutral'
  
  const diff = current - previous
  if (Math.abs(diff) < 2) return 'neutral' // Less than 2 points difference is neutral
  return diff > 0 ? 'up' : 'down'
}

/**
 * Get performance insights and recommendations
 */
export const getPerformanceInsights = (categories: CategoryData[]): {
  strengths: string[]
  improvements: string[]
  actions: string[]
} => {
  const sorted = [...categories].sort((a, b) => b.score - a.score)
  const strengths = sorted.slice(0, 3).filter(c => c.score >= 80).map(c => c.name)
  const improvements = sorted.slice(-3).filter(c => c.score < 70).map(c => c.name)
  
  const actions: string[] = []
  
  // Generate specific actions based on low-performing categories
  improvements.forEach(category => {
    switch (category) {
      case 'Comunicação':
        actions.push('Agendar treinamento de comunicação não-violenta')
        break
      case 'Processos & Wrike':
        actions.push('Revisar workflow e aderência às ferramentas')
        break
      case 'Técnico':
        actions.push('Definir plano de mentoria técnica')
        break
      default:
        actions.push(`Criar plano de desenvolvimento para ${category}`)
    }
  })

  return { strengths, improvements, actions }
}

/**
 * Generate real team data from employee service
 */
export const generateTeamData = async (userEmail: string): Promise<TeamMemberData[]> => {
  const user = (await getAllMonumentaEmployees()).find(emp => emp.email === userEmail);

  if (!user) {
    throw new Error(`User with email ${userEmail} not found`);
  }

  // Get real subordinates from data service
  const subordinates = await getCurrentUserSubordinates(userEmail);

  return subordinates.map((emp) => {
    // Map real performance to scores
    const score = emp.performance === 'ALTA PERFORMANCE' ? 85 + Math.floor(Math.random() * 15) :
                  emp.performance === 'POTENCIAL CRESCIMENTO' ? 75 + Math.floor(Math.random() * 15) :
                  emp.performance === 'DESENVOLVIMENTO' ? 65 + Math.floor(Math.random() * 15) :
                  emp.performance === 'CUMPRE FUNÇÃO' ? 60 + Math.floor(Math.random() * 10) :
                  emp.performance === 'SOB ANÁLISE' ? 50 + Math.floor(Math.random() * 15) :
                  45 + Math.floor(Math.random() * 15);

    const previousScore = Math.max(40, score - Math.floor(Math.random() * 20) + Math.floor(Math.random() * 10));
    const level = getPerformanceLevel(score).config.label;

    return {
      id: emp.id.toString(),
      name: emp.name,
      role: emp.role,
      score,
      previousScore,
      level,
      trend: calculateTrend(score, previousScore),
      keyMetric: {
        label: 'Performance',
        value: emp.performance || 'Indefinido',
        color: score >= 80 ? 'text-success' : score >= 65 ? 'text-warning' : 'text-error'
      },
      alertStatus: score < 50 ? 'urgent' as const : score < 70 ? 'attention' as const : 'none' as const,
      lastUpdated: '7 dias' // Could be calculated from real data
    }
  });
}

export const generateCategoriesData = async (userEmail: string): Promise<CategoryData[]> => {
  const user = (await getAllMonumentaEmployees()).find(emp => emp.email === userEmail);

  if (!user) {
    throw new Error(`User with email ${userEmail} not found`);
  }

  // Map real performance to category scores
  const performanceScore = user.performance === 'ALTA PERFORMANCE' ? 90 :
                          user.performance === 'POTENCIAL CRESCIMENTO' ? 80 :
                          user.performance === 'DESENVOLVIMENTO' ? 70 :
                          user.performance === 'CUMPRE FUNÇÃO' ? 65 :
                          user.performance === 'SOB ANÁLISE' ? 55 : 50;

  // Generate realistic variations around the base score
  return Object.entries(PERFORMANCE_CATEGORIES).map(([id, config]) => {
    const variation = Math.floor(Math.random() * 20) - 10; // ±10 points variation
    const score = Math.max(40, Math.min(100, performanceScore + variation));

    return {
      id,
      name: config.name,
      score,
      weight: config.weight,
      trend: score > performanceScore ? 'up' as const :
             score < performanceScore - 5 ? 'down' as const : 'neutral' as const,
      evidence: `Avaliação baseada na performance atual: ${user.performance}`,
      actions: score < 70 ?
        [`Melhorar ${config.name}`, `Treinamento em ${config.name}`] :
        [`Manter nível em ${config.name}`, `Compartilhar conhecimento em ${config.name}`]
    };
  });
};
